/**
 * AnthropicStreamHandler - Handles streaming responses from Anthropic API
 *
 * Decouples stream parsing from request making and UI management.
 * Provides real-time callbacks for React integration while remaining framework-agnostic.
 */

import {
  AnthropicEvent,
  AnthropicStreamOptions,
  ContentBlockDeltaEvent,
  ContentBlockStartEvent,
  ContentBlockStopEvent,
  MessageDeltaEvent,
  MessageStartEvent,
  ParsedSSEEvent,
  StreamResult,
  ToolCallComplete,
  ToolCallInProgress,
  ToolCallStart,
  ToolCallUpdate,
} from "./anthropic-stream-handler.types";

export class AnthropicStreamHandler {
  private buffer = "";
  private toolCalls = new Map<number, ToolCallInProgress>();
  private fullTextContent = "";
  private abortController?: AbortController;
  private isAborted = false;
  private usage?: { input_tokens: number; output_tokens: number };
  private stopReason?: "end_turn" | "tool_use";
  private messageId?: string;

  constructor(private options: AnthropicStreamOptions = {}) {}

  /**
   * Reset internal state for stream reuse
   */
  private resetState(): void {
    this.buffer = "";
    this.toolCalls.clear();
    this.fullTextContent = "";
    this.isAborted = false;
    this.usage = undefined;
    this.stopReason = undefined;
    this.abortController = undefined;
    this.messageId = undefined;
  }

  /**
   * Process a ReadableStream from Anthropic API
   */
  async processStream(stream: ReadableStream): Promise<StreamResult> {
    if (!stream) {
      throw new Error("ReadableStream is required");
    }

    // Reset state for fresh stream processing
    this.resetState();

    // Set up abort controller
    this.abortController = new AbortController();

    let reader: ReadableStreamDefaultReader | undefined;
    try {
      reader = stream.getReader();
      const decoder = new TextDecoder("utf-8");

      while (!this.isAborted) {
        const { value, done } = await reader.read();

        if (done) {
          break;
        }

        // Add chunk to buffer
        this.buffer += decoder.decode(value, { stream: true });

        // Process complete events
        this.processBufferEvents();
      }

      // If aborted, stop here without emitting completion events
      if (this.isAborted) {
        try {
          await reader.cancel();
        } catch (_ignore) {
          // ignore cancel errors
        }
        const abortErr = new Error("Stream aborted");
        abortErr.name = "AbortError";
        throw abortErr;
      }

      // Process any remaining buffer content
      this.processRemainingBuffer();

      // Build final result
      const result: StreamResult = {
        fullTextContent: this.fullTextContent,
        toolCalls: Array.from(this.toolCalls.values()).map(
          this.completeToolCall
        ),
        usage: this.usage,
        stopReason: this.stopReason,
        messageId: this.messageId,
      };

      // Notify completion
      this.options.onComplete?.(result);

      return result;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      // Don't report aborts as errors to consumers
      if (err.name !== "AbortError") {
        this.options.onError?.(err);
      }
      throw err;
    }
  }

  /**
   * Abort the stream processing
   */
  abort(): void {
    this.isAborted = true;
    this.abortController?.abort();
  }

  /**
   * Clean up resources and reset state
   */
  cleanup(): void {
    this.abort();
    this.resetState();
  }

  /**
   * Process complete events from buffer using smart SSE parsing
   * Works with both single newline and double newline separators
   */
  private processBufferEvents(): void {
    const completeEvents: string[] = [];
    const lines = this.buffer.split("\n");

    let currentEvent: string[] = [];

    for (const line of lines) {
      // Check if this line starts a new event
      if (line.startsWith("event:") && currentEvent.length > 0) {
        // We have a complete previous event, save it
        const eventStr = currentEvent.join("\n");
        if (eventStr.trim()) {
          completeEvents.push(eventStr);
        }
        // Start new event with current line
        currentEvent = [line];
      } else if (line.trim() || currentEvent.length > 0) {
        // Add line to current event (include empty lines if we're in an event)
        currentEvent.push(line);
      }
    }

    // Keep the last incomplete event in buffer
    this.buffer = currentEvent.join("\n");

    // Process each complete event
    for (const eventStr of completeEvents) {
      if (!eventStr.trim()) continue;

      try {
        const { eventType, data } = this.parseSSEEvent(eventStr);

        // Debug callback
        this.options.onEvent?.(eventType || "unknown", data as AnthropicEvent);

        // Handle the event
        if (eventType && data) {
          this.handleEvent(eventType, data as AnthropicEvent);
        }
      } catch (error) {
        console.warn("Failed to process event:", eventStr, error);
        // Continue processing other events
      }
    }
  }

  /**
   * Process any remaining buffer content at stream end
   */
  private processRemainingBuffer(): void {
    if (!this.buffer.trim()) return;

    try {
      const { eventType, data } = this.parseSSEEvent(this.buffer);

      if (eventType && data) {
        this.options.onEvent?.(eventType, data as AnthropicEvent);
        this.handleEvent(eventType, data as AnthropicEvent);
      }
    } catch (error) {
      console.warn("Failed to process remaining buffer:", this.buffer, error);
    }
  }

  /**
   * Parse SSE event format: event: type\ndata: json\n
   */
  private parseSSEEvent(chunk: string): ParsedSSEEvent {
    const lines = chunk.split("\n");
    let eventType: string | null = null;
    const dataLines: string[] = [];

    for (const line of lines) {
      if (line.startsWith("event: ")) {
        eventType = line.substring(7).trim();
      } else if (line.startsWith("data: ")) {
        dataLines.push(line.substring(6));
      }
    }

    const dataRaw = dataLines.join("\n").trim();
    let data: unknown = null;
    if (dataRaw) {
      try {
        data = JSON.parse(dataRaw);
      } catch (e) {
        console.warn(`Failed to parse JSON: ${dataRaw}`, e);
        data = dataRaw;
      }
    }

    return { eventType, data };
  }

  /**
   * Handle different event types
   */
  private handleEvent(eventType: string, data: AnthropicEvent): void {
    switch (eventType) {
      case "message_start":
        this.handleMessageStart(data as MessageStartEvent);
        break;

      case "content_block_start":
        this.handleContentBlockStart(data as ContentBlockStartEvent);
        break;

      case "content_block_delta":
        this.handleContentBlockDelta(data as ContentBlockDeltaEvent);
        break;

      case "content_block_stop":
        this.handleContentBlockStop(data as ContentBlockStopEvent);
        break;

      case "message_delta":
        this.handleMessageDelta(data as MessageDeltaEvent);
        break;

      case "message_stop":
        // Stream is ending - handled in processStream
        break;

      case "ping":
        // Keep-alive - no action needed
        break;

      case "error":
        this.handleErrorEvent(
          data as import("./anthropic-stream-handler.types").ErrorEvent
        );
        break;

      default:
        console.warn(`Unhandled event type: ${eventType}`, data);
    }
  }

  private handleMessageStart(event: MessageStartEvent): void {
    // Store initial usage data
    this.usage = {
      input_tokens: event.message.usage.input_tokens,
      output_tokens: event.message.usage.output_tokens,
    };

    // Capture Anthropic message id for stable UI keys
    this.messageId = event.message.id;
  }

  private handleContentBlockStart(event: ContentBlockStartEvent): void {
    if (event.content_block.type === "tool_use") {
      // Validate required fields
      if (!event.content_block.id || !event.content_block.name) {
        console.warn("Invalid tool_use block: missing id or name", event);
        return;
      }

      const toolCall: ToolCallInProgress = {
        index: event.index,
        id: event.content_block.id,
        name: event.content_block.name,
        arguments: "",
      };

      this.toolCalls.set(event.index, toolCall);

      // Notify tool call start
      const toolCallStart: ToolCallStart = {
        index: event.index,
        id: event.content_block.id,
        name: event.content_block.name,
      };
      this.options.onToolCallRequestStart?.(toolCallStart);
    }
  }

  private handleContentBlockDelta(event: ContentBlockDeltaEvent): void {
    if (event.delta.type === "text_delta") {
      // Validate text delta has text property
      if (typeof event.delta.text !== "string") {
        console.warn("Invalid text_delta: missing text property", event);
        return;
      }

      // Accumulate text content
      this.fullTextContent += event.delta.text;

      // Notify text delta
      this.options.onTextChunkDelta?.(event.delta.text, this.fullTextContent);
    } else if (event.delta.type === "input_json_delta") {
      // Validate partial_json property
      if (typeof event.delta.partial_json !== "string") {
        console.warn(
          "Invalid input_json_delta: missing partial_json property",
          event
        );
        return;
      }

      // Accumulate tool call arguments
      const toolCall = this.toolCalls.get(event.index);
      if (toolCall) {
        toolCall.arguments += event.delta.partial_json;
        // Emit progress update for tool call arguments
        const update: ToolCallUpdate = {
          index: toolCall.index,
          id: toolCall.id,
          name: toolCall.name,
          argumentsText: toolCall.arguments,
        };
        this.options.onToolCallRequestUpdate?.(update);
      } else {
        console.warn(`No tool call found for index ${event.index}`, event);
      }
    }
  }

  private handleContentBlockStop(event: ContentBlockStopEvent): void {
    const toolCall = this.toolCalls.get(event.index);
    if (toolCall) {
      try {
        // Parse completed tool arguments
        const parsedArgs = JSON.parse(toolCall.arguments || "{}");
        const completedToolCall: ToolCallComplete = {
          index: toolCall.index,
          id: toolCall.id,
          name: toolCall.name,
          arguments: parsedArgs,
        };

        // Update in map
        this.toolCalls.set(event.index, {
          ...toolCall,
          arguments: JSON.stringify(parsedArgs), // Keep as string in map
        });

        // Notify tool call complete
        this.options.onToolCallRequestComplete?.(completedToolCall);
      } catch (error) {
        console.error(
          `Failed to parse tool arguments: ${toolCall.arguments}`,
          error
        );
        this.options.onError?.(
          new Error(
            `Invalid tool call arguments: ${
              error instanceof Error ? error.message : error
            }`
          )
        );
      }
    }
  }

  private handleMessageDelta(event: MessageDeltaEvent): void {
    // Update stop reason and usage
    if (event.delta.stop_reason) {
      this.stopReason = event.delta.stop_reason;
    }

    if (this.usage && event.usage) {
      this.usage.output_tokens = event.usage.output_tokens;
    }
  }

  private handleErrorEvent(
    event: import("./anthropic-stream-handler.types").ErrorEvent
  ): void {
    const error = new Error(
      event.error?.message || "Unknown error from Anthropic API"
    );
    this.options.onError?.(error);
  }

  /**
   * Convert ToolCallInProgress to ToolCallComplete
   */
  private completeToolCall = (
    toolCall: ToolCallInProgress
  ): ToolCallComplete => {
    try {
      return {
        index: toolCall.index,
        id: toolCall.id,
        name: toolCall.name,
        arguments: JSON.parse(toolCall.arguments),
      };
    } catch {
      return {
        index: toolCall.index,
        id: toolCall.id,
        name: toolCall.name,
        arguments: {},
      };
    }
  };
}

export * from "./anthropic-stream-handler.types";
